'use strict';

const ALLOWED_REGIONS = ['DE', 'FI', 'NO', 'SE', 'KRI', 'SAN', 'EDS', 'FIN', 'BOK'];

class PromiseCache {
  /**
   * @param {number} cacheDuration - Duration in milliseconds for which the promise result should be cached.
   * Default is 5000ms (5 seconds).
   *
   */
  constructor(cacheDuration = 5000) {
    this.cache = new Map();
    this.cacheDuration = cacheDuration;
  }

  buildKey(keys) {
    if (typeof keys === 'string') {
      return keys;
    }
    if (Array.isArray(keys)) {
      return keys.join(':');
    }
    throw new Error('Invalid keys type. Expected string or array of strings.');
  }

  /**
   *
   * @param {() => Promise<any>} promiseProvider - A function that returns a promise.
   * @param {string | string[]} keys - A string or an array of strings to use as keys for caching.
   * @returns {Promise<any>} - The promise that resolves to the result of the promiseProvider.
   */
  get(promiseProvider, keys) {
    const key = this.buildKey(keys);
    if (!this.cache.has(key)) {
      const promise = promiseProvider()
        .catch((error) => {
          // Ensure that errors are not cached
          this.cache.delete(key);
          throw error;
        })
        .then((data) => {
          // Schedule cache invalidation
          setTimeout(() => {
            this.cache.delete(key);
          }, this.cacheDuration);
          return data;
        });
      this.cache.set(key, promise);
    }
    return this.cache.get(key);
  }
}

export class Packaging {
  /**
   * @param {string} url - The URL to fetch data from.
   * @param {object} options - Options for the packaging.
   * @param {string} options.warehouse - The warehouse for the packaging.
   * @param {string} options.region - The region for the packaging. Allowed values: 'DE', 'FI', 'NO', 'SE', 'KRI', 'SAN', 'EDS', 'BOK'.
   * @param {number} options.cache - Duration in milliseconds for which the promise result should be cached. Default is 5000ms (5 seconds).
   */
  constructor(url, { region, warehouse, cache }) {
    this.abortControllers = {};
    this.cache = new PromiseCache(cache);

    if (!url) {
      throw new Error('URL is required');
    }

    this.url = url;
    this.setRegion(region);
    this.setWarehouse(warehouse);
  }

  setRegion(region) {
    if (!ALLOWED_REGIONS.includes(region)) {
      throw new Error(`Invalid region: ${region}. Allowed regions are: ${ALLOWED_REGIONS.join(', ')}`);
    }
    this.region = region;
  }

  getRegion() {
    return this.region || '';
  }

  setWarehouse(warehouse) {
    if (!warehouse) {
      throw new Error('Warehouse is required');
    }
    this.warehouse = warehouse;
  }

  getWarehouse() {
    return this.warehouse || '';
  }

  async fetch(orderLines) {
    const url = new URL(this.url);
    url.searchParams.append('region', this.region);

    const requestId = this.cache.buildKey([
      'packaging',
      this.region,
      this.warehouse,
      orderLines.map((line) => Object.values(line).join('-')).join(','),
    ]);

    // Cancel other requests
    for (const key of Object.keys(this.abortControllers)) {
      if (key !== requestId) {
        this.cancel(key);
      }
    }

    return this.cache.get(() => {
      this.abortControllers[requestId] = new AbortController();
      return new Promise((resolve, reject) => {
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orderLines: orderLines.reduce((lines, line) => {
              lines.push({
                ...line,
                warehouse: this.warehouse,
              });
              return lines;
            }, []),
          }),
          signal: this.abortControllers[requestId].signal,
        })
          .then((response) => {
            if (response && response?.status !== 200) {
              reject(response);
            }
            return response.json();
          })
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              reject('cancelled');
            } else {
              reject(error);
            }
          });
      });
    }, requestId);
  }

  // Cancel the request
  // If cancelId is not provided, all requests will be cancelled
  cancel(requestId = '') {
    if (!requestId) {
      Object.keys(this.abortControllers).forEach((key) => {
        this.abortControllers[key].abort();
        delete this.abortControllers[key];
      });
      return;
    } else if (this.abortControllers[requestId]) {
      this.abortControllers[requestId].abort();
      delete this.abortControllers[requestId];
    }
  }
}
